type AliQtMethod = 'setMetaInfo' | 'appendMetaInfo' | 'record' | 'sendPV';
type AliQtApi = Partial<Record<AliQtMethod, (...args: unknown[]) => unknown>>;

declare global {
  interface Window {
    qt_queue?: {
      push(command: { action: string; arguments: unknown[] }): void;
    };
    qt?: AliQtApi;
  }
}

type AliQtProperties = Record<string, string | boolean | number | undefined>;
type AliQtPlatformType = 'web' | 'wechat_miniprogram';

const MAX_PROPERTY_STRING_LENGTH = 255;
let isAliQtEnabled = false;

export const resolveAliQtAppId = (platformType: AliQtPlatformType): string | undefined => {
  return platformType === 'wechat_miniprogram'
    ? process.env.NEXT_PUBLIC_ALI_QT_MINIAPP_APP_ID
    : process.env.NEXT_PUBLIC_ALI_QT_APP_ID;
};

const normalizeProperties = (properties: AliQtProperties): AliQtProperties =>
  Object.fromEntries(
    Object.entries(properties)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [
        key,
        typeof value === 'string' ? Array.from(value).slice(0, MAX_PROPERTY_STRING_LENGTH).join('') : value,
      ]),
  );

const dispatchQtCommand = (method: AliQtMethod, args: unknown[]): void => {
  if (typeof window === 'undefined' || !isAliQtEnabled || !process.env.NEXT_PUBLIC_ALI_QT_CHANNEL_DOMAIN) {
    return;
  }

  try {
    const api = window.qt;
    const apiMethod = api?.[method];
    if (typeof apiMethod === 'function') {
      apiMethod.apply(api, args);
      return;
    }

    window.qt_queue ||= [];
    window.qt_queue.push({ action: `qt.${method}`, arguments: args });
  } catch (error) {
    console.warn('[QT] Failed to dispatch analytics command', error);
  }
};

const getEventType = (eventName: string): 'CLK' | 'EXP' | 'OTHER' => {
  if (eventName.endsWith('_click') || eventName === 'click_validation_code') return 'CLK';
  if (/_(exposure|expose|show)$/.test(eventName) || eventName === 'order_modal_expsure') return 'EXP';
  return 'OTHER';
};

export const setAliQtGlobalProperties = (properties: AliQtProperties): void => {
  dispatchQtCommand('appendMetaInfo', ['globalproperty', normalizeProperties(properties)]);
};

export const initAliQt = (platformType: AliQtPlatformType, globalProperties: AliQtProperties = {}): void => {
  if (typeof window === 'undefined') return;

  const appId = resolveAliQtAppId(platformType);
  const channelDomain = process.env.NEXT_PUBLIC_ALI_QT_CHANNEL_DOMAIN;
  const debug = process.env.NEXT_PUBLIC_ALI_QT_DEBUG === 'true';
  if (!appId || !channelDomain) return;

  isAliQtEnabled = true;
  window.qt_queue ||= [];
  for (const args of [
    ['appKey', appId],
    ['DEBUG', debug],
    ['trackDomain', channelDomain],
    ['aplus-disable-apv', true],
    ['aplus-autotrack-enabled', false],
  ]) {
    dispatchQtCommand('setMetaInfo', args);
  }
  setAliQtGlobalProperties(globalProperties);

  if (document.getElementById('beacon-qt')) return;

  const script = document.createElement('script');
  script.async = true;
  script.id = 'beacon-qt';
  script.src = 'https://g.alicdn.com/QTSDK/qt-sdk-javascript/2.5.5/qt_web.cjs.js';
  document.head.appendChild(script);
};

export const reportAliQtEvent = (eventName: string, properties: AliQtProperties = {}): void => {
  const data = normalizeProperties(properties);
  dispatchQtCommand('record', [eventName, getEventType(eventName), data]);
};

export const aliQtSignUp = (id: string): void => {
  if (!id) return;
  dispatchQtCommand('setMetaInfo', ['_user_id', id]);
};

export const aliQtSignOut = (): void => {
  dispatchQtCommand('setMetaInfo', ['_user_id', '']);
};
